import React from 'react';
import styled from 'styled-components';

import Input from '../../components/Input';
import SubConfiguration from './SubConfiguration';
import {
  CODE_TYPES,
  JULIAN_FORMATS,
  DATE_CODE_FORMATS,
  getCodeGeneratorConfig,
  isSheetScopedCodeQuestion,
  createCodePreview,
  createSequencePreview,
  toStartingCode,
  fromStartingCode,
  CODE_PREVIEW_SAMPLE_DATE,
  DEFAULT_CODE_SEQ_START,
  MAX_CODE_AFFIX_LENGTH,
  MAX_CODE_SEQ_DIGITS
} from './shared';

function CodeGeneratorConfig({question, validations = {}, disabled = false, onMetaChange}) {
  const config = getCodeGeneratorConfig(question);
  const {codeType} = config;
  const isSequential = codeType === 'SEQUENTIAL';
  //"Show on setup" is what decides the scope, so the notices below have to follow it
  const isSheetScoped = isSheetScopedCodeQuestion(question);
  //a row question either counts on across every sheet (the default, stored as no value) or starts over on each one
  const restartsPerSheet = isSequential && !isSheetScoped && config.codeSeqScope === 'SHEET';

  // Switching code type clears the format, since the valid formats differ between
  // Julian Date and Date Code. Prefix and suffix are kept (the API supports them
  // for every code type).
  //moving to or from sequential swaps which settings apply, so the ones that no longer mean anything
  //are cleared and the sequence is seeded with a starting point
  const handleCodeTypeChange = e => {
    const nextType = e.target.value;

    if (nextType === 'SEQUENTIAL') {
      onMetaChange({codeType: nextType, codeFormat: '', ...fromStartingCode(DEFAULT_CODE_SEQ_START)});
      return;
    }

    onMetaChange({codeType: nextType, codeFormat: '', codeSeqStart: '', codeSeqPadding: '', codeSeqScope: ''});
  };

  const handleFieldChange = key => e => onMetaChange({[key]: e.target.value});

  //one box gives the api both settings, since the zeros in what they type decide the padding
  const handleStartingCodeChange = e => onMetaChange(fromStartingCode(e.target.value));

  const handleScopeChange = e => onMetaChange({codeSeqScope: e.target.value === 'SHEET' ? 'SHEET' : ''});

  const formats = codeType === 'JULIAN_DATE'
    ? JULIAN_FORMATS
    : codeType === 'DATE_CODE' ? DATE_CODE_FORMATS : [];

  const preview = createCodePreview(config, CODE_PREVIEW_SAMPLE_DATE);
  const sampleDateLabel = CODE_PREVIEW_SAMPLE_DATE.toLocaleDateString();
  const followingCodes = createSequencePreview(config);

  return (
    <Container>
      <Input
        id={`code-type-${question.id}`}
        type="select"
        label="Code type"
        disabled={disabled}
        value={codeType}
        onChange={handleCodeTypeChange}
        invalid={!!validations.codeType}
        validationText={validations.codeType || ''}
      >
        <option value="">Choose a code type</option>
        {CODE_TYPES.map(type => (
          <option key={type.value} value={type.value}>{type.label}</option>
        ))}
      </Input>

      {(codeType === 'JULIAN_DATE' || codeType === 'DATE_CODE') && (
        <SubConfiguration>
          <SubConfiguration.Item>
            <FieldGrid>
              <Input
                id={`code-format-${question.id}`}
                type="select"
                label="Format"
                required
                disabled={disabled}
                value={config.codeFormat}
                onChange={handleFieldChange('codeFormat')}
                invalid={!!validations.codeFormat}
                validationText={validations.codeFormat || ''}
              >
                <option value="">Choose a format</option>
                {formats.map(format => (
                  <option key={format.value} value={format.value}>{format.label}</option>
                ))}
              </Input>
              <Input
                id={`code-prefix-${question.id}`}
                type="text"
                label="Prefix"
                maxLength={MAX_CODE_AFFIX_LENGTH}
                disabled={disabled}
                value={config.codePrefix}
                onChange={handleFieldChange('codePrefix')}
                invalid={!!validations.codePrefix}
                validationText={validations.codePrefix || ''}
                helpText={`Optional. Up to ${MAX_CODE_AFFIX_LENGTH} characters.`}
              />
              <Input
                id={`code-suffix-${question.id}`}
                type="text"
                label="Suffix"
                maxLength={MAX_CODE_AFFIX_LENGTH}
                disabled={disabled}
                value={config.codeSuffix}
                onChange={handleFieldChange('codeSuffix')}
                invalid={!!validations.codeSuffix}
                validationText={validations.codeSuffix || ''}
                helpText={`Optional. Up to ${MAX_CODE_AFFIX_LENGTH} characters.`}
              />
            </FieldGrid>
          </SubConfiguration.Item>

          {preview && (
            <SubConfiguration.Item>
              <PreviewLabel>{`Example (for ${sampleDateLabel}):`}</PreviewLabel>
              <PreviewValue>{preview}</PreviewValue>
              <PreviewNote>Generated on the mobile device using its local date; values may repeat.</PreviewNote>
            </SubConfiguration.Item>
          )}
        </SubConfiguration>
      )}

      {isSequential && (
        <SubConfiguration>
          <SubConfiguration.Item>
            <FieldGrid>
              <Input
                id={`code-seq-start-${question.id}`}
                type="text"
                label="Starting code"
                required
                disabled={disabled}
                maxLength={MAX_CODE_SEQ_DIGITS}
                value={toStartingCode(config)}
                onChange={handleStartingCodeChange}
                invalid={!!validations.codeSeqStart}
                validationText={validations.codeSeqStart || ''}
                helpText="The first number issued. Leading zeros set the width, so 001 counts 001, 002, 003."
              />
              {!isSheetScoped && (
                <Input
                  id={`code-seq-scope-${question.id}`}
                  type="select"
                  label="Numbering"
                  disabled={disabled}
                  value={restartsPerSheet ? 'SHEET' : 'FORM'}
                  onChange={handleScopeChange}
                  helpText={restartsPerSheet
                    ? 'Every new sheet starts again from the starting code.'
                    : 'One series that keeps counting across every sheet.'}
                >
                  <option value="FORM">Continues across all sheets</option>
                  <option value="SHEET">Restarts on each sheet</option>
                </Input>
              )}
              <Input
                id={`code-prefix-${question.id}`}
                type="text"
                label="Prefix"
                maxLength={MAX_CODE_AFFIX_LENGTH}
                disabled={disabled}
                value={config.codePrefix}
                onChange={handleFieldChange('codePrefix')}
                invalid={!!validations.codePrefix}
                validationText={validations.codePrefix || ''}
                helpText={`Optional. Up to ${MAX_CODE_AFFIX_LENGTH} characters.`}
              />
              <Input
                id={`code-suffix-${question.id}`}
                type="text"
                label="Suffix"
                maxLength={MAX_CODE_AFFIX_LENGTH}
                disabled={disabled}
                value={config.codeSuffix}
                onChange={handleFieldChange('codeSuffix')}
                invalid={!!validations.codeSuffix}
                validationText={validations.codeSuffix || ''}
                helpText={`Optional. Up to ${MAX_CODE_AFFIX_LENGTH} characters.`}
              />
            </FieldGrid>
          </SubConfiguration.Item>

          {preview && (
            <SubConfiguration.Item>
              <PreviewLabel>Example:</PreviewLabel>
              <PreviewValue>
                {[preview, ...followingCodes].join(', ')}
                {followingCodes.length > 0 && <Continues>, …</Continues>}
              </PreviewValue>
              <PreviewNote>
                An example only, no number is reserved by editing this form.
              </PreviewNote>
            </SubConfiguration.Item>
          )}

          <SubConfiguration.Item>
            <Notice>
              <NoticeTitle>
                {isSheetScoped ? 'One number per sheet' : 'One number per submission'}
              </NoticeTitle>
              {isSheetScoped
                ? `Because this question is shown on setup, one number is issued when the sheet is created
                   and every submission on that sheet shares it. Untick "Show on setup" to issue a new
                   number for each submission instead.`
                : restartsPerSheet
                  ? `A new number is issued for every submission, counting from the starting code again on
                     each sheet, so the same number appears on every sheet. Pair it with something that
                     identifies the sheet, such as a lot or day code, wherever it has to be traceable.`
                  : `A new number is issued for every submission. Tick "Show on setup" to issue one number
                     per sheet instead, shared by every submission on it.`}
            </Notice>
          </SubConfiguration.Item>

          <SubConfiguration.Item>
            <Notice>
              <NoticeTitle>This code needs a connection</NoticeTitle>
              {isSheetScoped
                ? `The number is issued by the server, so no two sheets can ever get the same one. Devices
                   cannot create a sheet on this form while offline, though submissions afterwards work as
                   normal.`
                : `Each number is issued by the server, so no two people can ever get the same one. Devices
                   cannot submit rows for this form while offline.`}
            </Notice>
          </SubConfiguration.Item>

          <SubConfiguration.Item>
            <Notice>
              <NoticeTitle>Settings lock once the form is published</NoticeTitle>
              After publishing, the starting code, its width and the numbering can no longer be changed.
              Numbers already issued may be printed on product, and moving the start point could repeat one.
            </Notice>
          </SubConfiguration.Item>
        </SubConfiguration>
      )}
    </Container>
  );
}

const Container = styled.div`
  padding: 1rem;
  background: #f5f5f5;
  border-radius: 4px;
  margin: 1rem 0;

  > * {
    margin-top: 0.75rem;
  }

  > *:first-child {
    margin-top: 0;
  }
`;

const FieldGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 1rem;
  white-space: normal;
`;

const PreviewLabel = styled.div`
  font-size: 0.875rem;
  color: #666;
  margin-bottom: 0.25rem;
`;

const PreviewValue = styled.div`
  font-family: monospace;
  font-size: 1.125rem;
  font-weight: 600;
  color: #111;
`;

const Continues = styled.span`
  color: #999;
  font-weight: 400;
`;

const Notice = styled.div`
  font-size: 0.8125rem;
  line-height: 1.4;
  color: #555;
  background: #fff;
  border-left: 3px solid #0049ee;
  border-radius: 2px;
  padding: 0.5rem 0.75rem;
  white-space: normal;
`;

const NoticeTitle = styled.div`
  font-weight: 600;
  color: #111;
  margin-bottom: 0.125rem;
`;

const PreviewNote = styled.div`
  font-size: 0.75rem;
  color: #999;
  font-style: italic;
  margin-top: 0.25rem;
  white-space: normal;
`;

export default CodeGeneratorConfig;
